import React, { useEffect, useRef, useState } from "react";

/**
 * 動画をタップしたときの拡大プレイヤー。
 *
 * ページ全体を暗くせず、右下にカードとして出す（vidget 風）。
 * 訪問者がページを見たまま動画を見られるので、離脱感が少ない。
 *
 * 構成: ×（右上）/ ミュート（その下）/ 動画 / CTA 2つ / Powered by ノンアポ
 */

const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const IconMuted = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);

const IconUnmuted = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
    <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

// カード幅は固定。動画の高さはアスペクトから決め、親の画面高からはみ出さないよう抑える。
const CARD_WIDTH = 340;
const CHROME_HEIGHT = 170; // CTA 2つ ＋ Powered by ぶんの目安

const ExpandedVideo = (props) => {
  const {
    url,
    vertical,
    themeColor,
    online,
    mainButtonLabel,
    parentHeight,
    onClose,
    onStartMeeting,
    onOpenChat,
  } = props;

  const videoRef = useRef(null);
  // モバイルの自動再生要件があるので初期は必ずミュート
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const natural = vertical
    ? (CARD_WIDTH * 16) / 9
    : (CARD_WIDTH * 9) / 16;
  const room = (parentHeight || 900) - CHROME_HEIGHT - 40;
  const mediaHeight = Math.max(180, Math.min(natural, room));

  const toggleMute = (event) => {
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  // 動画クリックで再生／一時停止
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => undefined);
    else video.pause();
  };

  return (
    <div className="aisdr-vp2" style={{ "--aisdr-accent": themeColor }}>
      <div className="aisdr-vp2-media" style={{ height: `${mediaHeight}px` }}>
        <video
          ref={videoRef}
          src={url}
          onClick={togglePlay}
          autoPlay
          loop
          muted
          playsInline
          webkit-playsinline=""
        />
        <button
          type="button"
          className="aisdr-vp2-btn x"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          aria-label="閉じる"
        >
          <IconClose />
        </button>
        <button
          type="button"
          className="aisdr-vp2-btn mute"
          onClick={toggleMute}
          aria-label="ミュート切替"
        >
          {muted ? <IconMuted /> : <IconUnmuted />}
        </button>
        {/* 動画から CTA へのつなぎ。vidget と同じく下端をぼかす */}
        <div className="aisdr-vp2-fade" />
      </div>

      <div className="aisdr-vp2-cta">
        <button
          type="button"
          className="primary"
          disabled={!online}
          onClick={onStartMeeting}
        >
          <span>{mainButtonLabel || "アポなし商談"}</span>
          <span className="chev">›</span>
        </button>
        <button type="button" className="secondary" onClick={onOpenChat}>
          <span>AIに相談する</span>
          <span className="chev">›</span>
        </button>
      </div>

      <a
        className="aisdr-vp2-footer"
        href="https://non-appoint.com/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Powered by <b>AI SDR “ノンアポ”</b>
      </a>
    </div>
  );
};

export default ExpandedVideo;
